import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Company } from '@classes/company';
import { CompanyDetail } from '@classes/company-detail';
import { Contract } from '@classes/contract';
import { ContractGridViewItem } from '@classes/contract-grid-view-item';
import { MessageBoxParameter } from '@classes/message-box-parameter';
import { Role } from '@enums/role.enum';
import { MessageBox } from '@components/message-box/message-box.component';
import { TranslateService } from '@ngx-translate/core';
import { CompanyService } from 'app/core/services/company.service';
import { UiService } from 'app/core/services/ui.service';
import { ContractCreationComponent } from '../contract-creation/contract-creation.component';

@Component({
  selector: 'app-customer-detail',
  templateUrl: './customer-detail.component.html',
  styleUrls: ['./customer-detail.component.scss'],
  styles: [`
  >>> app-customer-detail {
    width: 100%;
    height: 100%;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
  }
`]
})
export class CustomerDetailComponent implements OnInit {

  customerForm: FormGroup;
  companyDetail: CompanyDetail;

  Role = Role;

  @Input() set companyDetail_(companyDetail: CompanyDetail) {
    this.companyDetail = companyDetail;
    if (companyDetail?.company) {
      this.companyDetail.company = companyDetail?.company;
      this.dataSource.data = (companyDetail?.company.contracts || [])
        .map(m => new ContractGridViewItem({ id: m.id, name: m.site?.name }))
      this.initFormBuilder(companyDetail?.company);
    }
    else {
      this.initFormBuilder();
    }
  };


  dataSource = new MatTableDataSource<ContractGridViewItem>();
  pageSizeOptions = [25, 50, 100];
  selectedRowID: string;
  displayedColumns: string[] = [
    'name',
    'action',
  ];

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  // @ViewChild(MatPaginator, { static: true }) MatPaginatorContract: MatPaginator;

  @Output() companyDetailEmit = new EventEmitter<any>();
  @Output() contractEmit = new EventEmitter<Contract>();
  @Output() refreshEmit = new EventEmitter<any>();


  constructor(
    private formBuilder: FormBuilder,
    public uiService: UiService,
    public companyService: CompanyService,
    public translateService: TranslateService,
    public dialog: MatDialog
  ) {
    this.companyDetail = new CompanyDetail();
  }

  ngOnInit() {
    this.initFormBuilder();
  }

  private initFormBuilder(value?: Company) {
    var editMode = !(this.companyDetail?.isEditMode || this.companyDetail?.isCreationMode);
    const fb = this.formBuilder;
    this.customerForm = fb.group({
      id: fb.control(value?.id),
      // number: fb.control({ value: value?.number, disabled: true }, []),
      name: fb.control({ value: value?.name, disabled: editMode }, [Validators.required]),
      email: fb.control({ value: value?.email, disabled: editMode }, [Validators.required]),
      ntva: fb.control({ value: value?.ntva, disabled: editMode }, [Validators.required]),
      contactName: fb.control({ value: value?.contactName, disabled: editMode }, [Validators.required]),
      address: fb.group({
        id: fb.control(value?.address?.id),
        address1: fb.control({ value: value?.address?.address1, disabled: editMode }, [Validators.required]),
        city: fb.control({ value: value?.address?.city, disabled: editMode }, [Validators.required]),
        zip: fb.control({ value: value?.address?.zip, disabled: editMode }, [Validators.required]),
      }),
      modifiedDate: fb.control({ value: null, disabled: true }, []),
      modifiedUser: fb.control({ value: null, disabled: true }, []),
    }); 
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    // this.dataSource.paginator = this.MatPaginatorContract;
  }

  selectItemClick(item: ContractGridViewItem) {
    this.selectedRowID = item.id || '';
  }

  goToDetail(item: ContractGridViewItem) {
    this.contractEmit.emit(new CompanyDetail({
      company: this.companyDetail.company,
      selectedContractID: item.id
    }));
  }

  onSubmit() {
    const company = new Company(
      this.customerForm.value
    )
    this.uiService.addRouteLoading();
    this.companyService.createOrUpdateCompany(company).subscribe(data => {
      this.companyDetail.isEditMode = false;
      this.companyDetail.isCreationMode = false;
      this.companyDetail.isReadOnlyMode = true;
      this.companyDetail.company = data;
      this.companyDetailEmit.emit();
      this.initFormBuilder(data);
      this.uiService.displayMessageToast('customer.success-edit');
      this.uiService.removeRouteLoading();
    }, error => {
      console.error(error);
      this.uiService.removeRouteLoading();
    })
  }
  onCancel() {
    this.companyDetail.isEditMode = false;
    this.companyDetail.isReadOnlyMode = true;
    if (this.companyDetail?.id) {
      this.uiService.addRouteLoading();
      this.companyService.getCompany(this.companyDetail?.id).subscribe(company => {
        this.initFormBuilder(company);
        this.uiService.removeRouteLoading();
      }, error => {
        console.error(error);
        this.uiService.removeRouteLoading();
      })
    }
  }
  onEdit() {
    this.companyDetail.isEditMode = true;
    this.companyDetail.isReadOnlyMode = false;
    this.initFormBuilder(this.companyDetail.company);
  }
  onDelete() {

    if (this.companyDetail && this.companyDetail?.id) {
      const messageBoxParameter = new MessageBoxParameter();

      messageBoxParameter.message = this.translateService.instant('customer.confirm-delete');
      messageBoxParameter.dialog = this.dialog;
      messageBoxParameter.title = 'Confirmation';
      messageBoxParameter.buttons = ['yes', 'no'];
      messageBoxParameter.width = "400px";

      MessageBox.show(messageBoxParameter).subscribe(mbResult => {
        if (mbResult && mbResult.value) {
          this.uiService.addRouteLoading();
          this.companyService.deleteCompany(this.companyDetail?.id || "").subscribe(data => {
            this.uiService.displayMessageToast('customer.success-delete');
            this.companyDetail.isEditMode = false;
            this.companyDetail.isReadOnlyMode = true;
            this.initFormBuilder();
            this.companyDetailEmit.emit();
            this.uiService.removeRouteLoading();
          }, error => {
            console.error(error);
            this.uiService.removeRouteLoading();
          });
        }
      });
    }
  }

  onCreateContract() {
    const data = new Contract({
      company: this.companyDetail.company
    });
    const dialogRef = this.dialog.open(ContractCreationComponent, {
      data,
      hasBackdrop: true
    });

    dialogRef.afterClosed().subscribe(refresh => {
      if (refresh) {
        this.refreshEmit.emit();
        this.uiService.displayMessageToast('contract.success-create');
      }
    })
  }

  isRequiredField(field: string) {
    const form_field = <AbstractControl>this.customerForm.get(field);
    if (!form_field.validator) {
      return false;
    }

    const validator = form_field.validator({} as AbstractControl);
    return (validator && validator.required);
  }
}
